'use client';

import { useEffect, useRef, useState, type ReactElement } from 'react';
import {
  getCartAnalyticsContext,
  getCartAnalyticsTiers,
  useAnalyticsEvent,
  useCartService,
  useRegionCurrency,
  type CartResponseResponse,
} from '@skin-seoul/service';
import { ImpressionArea, useCallbackRef } from '@skin-seoul/utils';
import { useCartDrawer } from '../../../context/CartDrawerContext';
import { useCartDiscovery } from './CartDiscoveryNavigation';
import { getCartBenefits } from './cartBenefits';

type Cart = CartResponseResponse['cart'];
type Location = 'cart_drawer' | 'unlock_reward_page';
export interface CartCardAttribution {
  placement:
    | 'bag_reward_card'
    | 'unlock_reward_page'
    | 'cart-upsell'
    | 'cart_skin_routine'
    | 'cart_wishlist'
    | 'cart_recently_viewed';
  position: number;
  tab?: string;
  isDetail?: boolean;
  strategy?: string;
  attributionToken?: string;
}
export function useCartAnalytics() {
  const analytics = useAnalyticsEvent();
  const { currency } = useRegionCurrency();
  const { getIsCartOpen } = useCartDrawer();
  const { useGetCartQuery } = useCartService();
  const { data } = useGetCartQuery({ currency }, { enabled: false });
  const cart = data?.cart;
  const track = useCallbackRef(
    (name: string, params: Record<string, unknown> = {}, location: Location = 'cart_drawer') => {
      if (!cart) {
        return;
      }
      analytics.trackCustom({
        eventName: name,
        ...(name === 'button_pressed_x_checkout' ? { gtmEventName: name } : { skipGTM: true }),
        params: { ...getCartAnalyticsContext(cart, location), ...params },
      });
    },
  );
  const mutation = useCallbackRef(
    (before: Cart | undefined, after: Cart, placement: string, location: Location = 'cart_drawer') => {
      if (!getIsCartOpen() || !before || before.currency.currency !== after.currency.currency) {
        return;
      }
      const previous = getCartAnalyticsTiers(before);
      const beforeContext = getCartAnalyticsContext(before, location);
      for (const tier of getCartAnalyticsTiers(after)) {
        const oldTier = previous.find(({ id, minAmount }) => id === tier.id && minAmount === tier.minAmount);
        if (!oldTier || oldTier.available === tier.available) {
          continue;
        }
        analytics.trackCustom({
          eventName: tier.available ? 'progress_bar_interaction' : 'bmsm_tier_lost',
          skipGTM: true,
          params: {
            ...getCartAnalyticsContext(after, location),
            ...(tier.available ? { action: 'completed', progress_name: 'free_shipping_bmsm' } : {}),
            source_component: 'BMSMCampaignBanner',
            previous_tier: beforeContext.current_tier,
            tier: tier.minAmount,
            trigger_placement: placement,
            ...(tier.available
              ? {
                  headline: `${getCartBenefits(after.progressBar, after.currency.currency).benefits.find(({ id }) => id === tier.id)?.title ?? 'Reward'} unlocked!`,
                }
              : {}),
          },
        });
      }
    },
  );
  return { cart, track, mutation };
}

/** Observe the existing DOM node; no extra wrapper or layout changes. */
export function CartAnalyticsImpression({
  children,
  onImpression,
  isDetail = false,
  impressionKey = '',
}: {
  children: ReactElement;
  onImpression: () => void;
  isDetail?: boolean;
  impressionKey?: string;
}) {
  const { isOpen } = useCartDrawer();
  const { isDetailOpen } = useCartDiscovery();
  const active = isOpen && Boolean(isDetailOpen) === isDetail;
  const [visible, setVisible] = useState(false);
  const fired = useRef<string | null>(null);
  const emit = useCallbackRef(onImpression);
  useEffect(() => {
    if (!isOpen) {
      fired.current = null;
    }
    if (!active || !visible || fired.current === impressionKey) {
      return;
    }
    const timer = setTimeout(() => {
      fired.current = impressionKey;
      emit();
    }, 1000);
    return () => clearTimeout(timer);
  }, [isOpen, active, visible, impressionKey, emit]);
  return (
    <ImpressionArea
      areaThreshold={0.5}
      timeThreshold={0}
      onImpressionStart={() => setVisible(true)}
      onImpressionEnd={() => setVisible(false)}
    >
      {children}
    </ImpressionArea>
  );
}

export function useCartCardTracking(product: { productId: number }, attribution?: CartCardAttribution) {
  const analytics = useAnalyticsEvent();
  const { cart } = useCartAnalytics();
  const location = attribution?.placement === 'unlock_reward_page' ? 'unlock_reward_page' : 'cart_drawer';
  const metadata: Record<string, unknown> = {};
  for (const key of ['strategy', 'attributionToken', 'recommendationOrigin'] as const) {
    if (key in product) {
      metadata[key] = (product as unknown as Record<string, unknown>)[key];
    }
  }
  const params = {
    ...(cart ? getCartAnalyticsContext(cart, location) : {}),
    ...metadata,
    ...(attribution?.strategy ? { strategy: attribution.strategy } : {}),
    ...(attribution?.attributionToken ? { attributionToken: attribution.attributionToken } : {}),
    ...(attribution
      ? {
          placement: attribution.placement,
          position: attribution.position,
          ...(attribution.tab ? { tab: attribution.tab } : {}),
        }
      : {}),
  };
  const event = {
    productId: String(product.productId),
    sectionName: attribution?.placement,
    position: attribution?.position,
    params,
  };
  return {
    params,
    click: () => {
      if (attribution) {
        analytics.trackClick(event);
      }
    },
    impression: () => {
      if (attribution) {
        analytics.trackImpression(event);
      }
    },
  };
}

export function CartSectionAnalytics({
  children,
  placement,
  count,
}: {
  children: ReactElement;
  placement: string;
  count: number;
}) {
  const { track } = useCartAnalytics();
  return (
    <CartAnalyticsImpression
      impressionKey={placement}
      onImpression={() => track('section_impression', { section_name: placement, page: 'cart', product_count: count })}
    >
      {children}
    </CartAnalyticsImpression>
  );
}
